import { ErrorComponent } from "@refinedev/antd";
import "@refinedev/antd/dist/reset.css";
import { Authenticated, HttpError, Refine } from "@refinedev/core";
import { RefineKbar, RefineKbarProvider } from "@refinedev/kbar";
import routerBindings, {
  CatchAllNavigate,
  UnsavedChangesNotifier,
} from "@refinedev/react-router-v6";
import dataProvider from "@refinedev/simple-rest";
import * as Sentry from "@sentry/react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import axios from "axios";
import { useEffect, useRef } from "react";
import {
  BrowserRouter,
  Navigate,
  Outlet,
  Route,
  Routes,
  useLocation,
} from "react-router-dom";

import { RESOURCES } from "./App.constants";
import {
  makeAuthProvider,
  REFRESH_KEY,
  TOKEN_KEY,
} from "./components/providers/authProvider";
import { NotificationContextProvider } from "./components/providers/notification/contextNotification";
import { smartNotificationProvider } from "./components/providers/notification/smartNotification";
import { useNotification } from "./components/providers/notification/useNotification";
import { ThemedLayout } from "./components/themedLayout";
import { ThemedHeader } from "./components/themedLayout/header";
import { ColorModeContextProvider } from "./contexts/color-mode";
import { ThemedBankManagement } from "./pages/bank-management";
import { ThemedBenificiaryRemitterDetails } from "./pages/benificiary-remitter/details";
import { ThemedBenificiaryRemitterPage } from "./pages/benificiary-remitter/management";
import { ThemedChangePasswordPage } from "./pages/change-password";
import { ConfirmEmail } from "./pages/confirmEmail";
import { ForgotPassword } from "./pages/forgotPassword";
import { ThemedHistory } from "./pages/history";
import { HomePage } from "./pages/home";
import { Login } from "./pages/login";
import { ThemedProfilePage } from "./pages/profile";
import { Register } from "./pages/register";
import { ThemedRequestMoney } from "./pages/request-money";
import { ResetPassword } from "./pages/resetPassword";
import { ThemedSendMoney } from "./pages/send-money";
import { setPermission } from "./store/use-permission";
import { useGlobalQueryErrorHandler } from "./utils/hooks/handleServerError";
import { socketConnect, socketUpdateToken } from "./utils/socket";
import { TronWebProvider } from "./components/TronWebProvider";

const queryClient = new QueryClient();
const SentryRoutes = Sentry.withSentryReactRouterV6Routing(Routes);

export const ROOT_URL = process.env.REACT_APP_ROOT_URL || "";
export const SmartAuthProvider = makeAuthProvider(ROOT_URL);

export const httpClient = axios.create({
  transformRequest: [
    function (data, headers) {
      if (headers) {
        headers["Authorization"] = `Bearer ${localStorage.getItem(TOKEN_KEY)}`;
      }
      return JSON.stringify(data);
    },
  ],
  headers: {
    "Content-Type": "application/json",
  },
});

// --- Variable using refresh ---
let isRefreshing = false;
let failedQueue: any[] = [];

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) prom.reject(error);
    else prom.resolve(token);
  });
  failedQueue = [];
};

httpClient.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    const originalRequest = error.config as any;
    const customError: HttpError = {
      ...error,
      message: error.response?.data?.message,
      statusCode: error.response?.status,
      reason_code: error.response?.data?.reason_code,
    };
    if (!error.response || customError.statusCode !== 401) {
      return Promise.reject(customError);
    }

    if (isRefreshing) {
      return new Promise(function (resolve, reject) {
        failedQueue.push({ resolve, reject });
      })
        .then((token) => {
          if (!originalRequest.headers) originalRequest.headers = {};
          originalRequest.headers.Authorization = `Bearer ${token}`;
          // Parse string
          if (typeof originalRequest.data === "string") {
            try {
              originalRequest.data = JSON.parse(originalRequest.data);
            } catch (e) {
              console.error(e);
            }
          }
          return httpClient(originalRequest);
        })
        .catch((err) => Promise.reject(err));
    }
    // Flag refreshing
    isRefreshing = true;
    const refreshToken = localStorage.getItem(REFRESH_KEY);

    if (!refreshToken) {
      processQueue(null, null);
      isRefreshing = false;
      // No refresh token
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(REFRESH_KEY);
      setPermission(false);
      return Promise.reject(customError);
    }

    try {
      // Call API refresh
      const res = await axios.post(
        `${ROOT_URL}/api/v1/auth/refresh-token`,
        undefined,
        {
          headers: {
            "x-refresh-token": refreshToken,
          },
        }
      );
      const newAccessToken = res.data.data["access_token"];
      const newRefreshToken = res.data.data["refresh_token"];
      localStorage.setItem(TOKEN_KEY, newAccessToken);
      if (newRefreshToken) localStorage.setItem(REFRESH_KEY, newRefreshToken);

      // Update default header
      httpClient.defaults.headers.common.Authorization = `Bearer ${newAccessToken}`;
      // Update socket token và reconnect mà không mất listeners
      socketUpdateToken(newAccessToken);

      // Call all API request in queue
      processQueue(null, newAccessToken);

      // Retry root request
      originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
      // Parse string
      if (typeof originalRequest.data === "string") {
        try {
          originalRequest.data = JSON.parse(originalRequest.data);
        } catch (e) {
          console.error(e);
        }
      }
      return httpClient(originalRequest);
    } catch (err) {
      processQueue(err, null);
      // Refresh fail → logout
      setPermission(false);
      return Promise.reject(err);
    } finally {
      isRefreshing = false;
    }
  }
);

const InnerApp = () => {
  const hasConnectedRef = useRef(false);

  // const location = useLocation();
  // timeout is 15 minutes by default and 100 minutes in development
  // const timeToLogoutSec =
  //   60 * (process.env.REACT_APP_ENVIRONMENT === "development" ? 2 : 10);
  // const timeoutPath = "/login";
  // const { resetTimeoutTime } = useDetectActivity(
  //   timeToLogoutSec,
  //   timeoutPath,
  //   location
  // );

  // useEffect(() => {
  //   resetTimeoutTime();
  // }, [location, resetTimeoutTime]);

  // httpClient.interceptors.response.use((response) => {
  //   const url = response.config.url;
  //   const isSocketOrPolling =
  //     url?.includes("/exchange-rates/bulk") ||
  //     url?.includes("/asset-config-groups/get-total-assets") ||
  //     url?.includes("/exchange-rates") ||
  //     url?.includes("/fee-configuration");
  //   if (!isSocketOrPolling) {
  //     resetTimeoutTime();
  //   }

  //   return response;
  // });

  const { open: openNotification } = useNotification();

  const queryCache = useGlobalQueryErrorHandler(openNotification);

  const queryClient = new QueryClient({
    queryCache,
    defaultOptions: {
      queries: {
        retry: 1,
        refetchOnWindowFocus: false,
      },
    },
  });

  useEffect(() => {
    if (!hasConnectedRef.current) {
      const { socket } = socketConnect(
        localStorage.getItem(TOKEN_KEY) ?? undefined
      );
      if (socket) {
        hasConnectedRef.current = true;
        socket.connect();
      }
    }
  }, []);

  return (
    <Refine
      dataProvider={dataProvider(ROOT_URL, httpClient)}
      notificationProvider={smartNotificationProvider as any}
      routerProvider={routerBindings}
      authProvider={SmartAuthProvider}
      resources={RESOURCES}
      options={{
        breadcrumb: false,
        syncWithLocation: true,
        warnWhenUnsavedChanges: true,
        reactQuery: {
          devtoolConfig: false,
          clientConfig: queryClient,
        },
        disableTelemetry: true,
      }}
    >
      <SentryRoutes>
        <Route
          element={
            <Authenticated
              key="fallback_to_login"
              fallback={<CatchAllNavigate to="/login" />}
            >
              <ThemedLayout Header={ThemedHeader}>
                <Outlet />
              </ThemedLayout>
            </Authenticated>
          }
        >
          <Route index element={<Navigate to={"/home"} />} />
          <Route path="/home">
            <Route index element={<HomePage />} />
          </Route>
          <Route path="/beneficiaries">
            <Route index element={<ThemedBenificiaryRemitterPage />} />
            <Route path=":id" element={<ThemedBenificiaryRemitterDetails />} />
          </Route>
          <Route path="/bank">
            <Route index element={<ThemedBankManagement />} />
          </Route>
          <Route path="/history">
            <Route index element={<ThemedHistory />} />
          </Route>
          <Route path="/send-money">
            <Route index element={<ThemedSendMoney />} />
          </Route>
          <Route path="/request-money">
            <Route index element={<ThemedRequestMoney />} />
          </Route>
          <Route path="/profile">
            <Route index element={<ThemedProfilePage />} />
          </Route>
          <Route path="/change-password">
            <Route index element={<ThemedChangePasswordPage />} />
          </Route>
        </Route>
        <Route
          element={
            <Authenticated key="fallback_to_outlet" fallback={<Outlet />}>
              <Navigate to="/home" />
            </Authenticated>
          }
        >
          <Route path="/login" element={<Login />} />
          <Route path="/login/:displayMode" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/confirm-email" element={<ConfirmEmail />} />
        </Route>
        <Route
          element={
            <Authenticated key="header">
              <ThemedLayout Header={ThemedHeader}>
                <Outlet />
              </ThemedLayout>
            </Authenticated>
          }
        >
          <Route path="*" element={<ErrorComponent />} />
        </Route>
      </SentryRoutes>
      <RefineKbar />
      <UnsavedChangesNotifier />
    </Refine>
  );
};

function App() {
  return (
    <BrowserRouter basename={process.env.PUBLIC_URL}>
      <RefineKbarProvider>
        <NotificationContextProvider
          open={smartNotificationProvider.open}
          close={smartNotificationProvider.close}
        >
          <ColorModeContextProvider>
            <QueryClientProvider client={queryClient}>
              <TronWebProvider>
                <InnerApp />
              </TronWebProvider>
            </QueryClientProvider>
          </ColorModeContextProvider>
        </NotificationContextProvider>
      </RefineKbarProvider>
    </BrowserRouter>
  );
}

export default App;
